import React from 'react'
import { Link } from 'react-router-dom'
import * as Helper from '../../../../../../util/Helper';
import StatusCode from '../../../../../../util/StatusCode';
import { DnpActivity, CoreDnpActivityListing, CoreDnpExportActivityMapping } from '../../../../../../models/CoreDnpModels';
import { SearchableOptionItem } from '../../../../../../util/UtilityTypes';
import { GetSchoolYear } from './DnpActivitiesUtil';
import { CacheRequestState, RequestState } from '../../../../../../models/RequestState';
import { PageResult } from '../../../../../../models/CoreModels';
import MultiSelectInput from '../../../../../misc/MultiSelectInput';
import { useTranslation } from 'react-i18next';


interface UnmappedDnpActivitiesProps {
    dnpExportId: string
    dnpSchoolUnitId: string
    dnpActivities: RequestState<DnpActivity[]>
    updatedMappedActivities: CoreDnpExportActivityMapping[] 
    onUpdate: (activities: CoreDnpExportActivityMapping[]) => void
    activitiesBySchool: CacheRequestState<PageResult<CoreDnpActivityListing>>
    disabled: boolean
    filter: {
        schoolType?: string
        schoolYear?: number[]
    }
}

const UnmappedDnpActivities = (props: UnmappedDnpActivitiesProps) => {
        const { t } = useTranslation(['configuration', 'common'])
    
    const updateActivity = (dnpActivityIds: string[] | undefined | null, flowSyncActivity: CoreDnpActivityListing) => {
        if (props.dnpActivities.code !== StatusCode.COMPLETE) {
            return; // can't update mappings if no dnp activities has been loaded
        }

        const dnpActivities = props.dnpActivities.data
            .filter(x => dnpActivityIds && dnpActivityIds.indexOf(x.id) !== -1);

        const updatedMappings: CoreDnpExportActivityMapping[] = dnpActivities.map(dnpActivity => ({
            dnpExportId: props.dnpExportId,
            dnpSchoolUnitId: props.dnpSchoolUnitId,
            dnpActivityId: dnpActivity.id,
            dnpActivityTitle: dnpActivity.displayName,
            dnpActivitySchoolType: dnpActivity.schoolType,
            flowSyncActivityId: flowSyncActivity.id,
            valid: true,
        }));

        let otherMappings: CoreDnpExportActivityMapping[] = props.updatedMappedActivities
            .filter(x => x.flowSyncActivityId !== flowSyncActivity.id);

        props.onUpdate([...otherMappings, ...updatedMappings])
    };

    const selected = (activityId: string) => {
        return props.updatedMappedActivities &&
            props.updatedMappedActivities
                .filter(x => x.flowSyncActivityId === activityId)
                .map(x => x.dnpActivityId);
    };

    return (<>
        {props.activitiesBySchool.code === StatusCode.COMPLETE && props.activitiesBySchool.data.values.map(a => {
            const selectedDnpActivity = selected(a.id);

            let dnpActivityOptions: SearchableOptionItem<string>[] = [];
            if (props.dnpActivities.code === StatusCode.COMPLETE) {
                dnpActivityOptions = props.dnpActivities.data
                    .filter(da => {
                        //always include previously selected
                        if (selectedDnpActivity.some(x => x === da.id)) {
                            return true;
                        }

                        //apply schooltype filter
                        if (props.filter.schoolType && da.schoolType !== props.filter.schoolType) {
                            return false;
                        }

                        //apply schoolYear filter
                        if (props.filter.schoolYear && props.filter.schoolYear?.length > 0 && !props.filter.schoolYear.includes(da.schoolYear)) {
                            return false;
                        }

                        return true;
                    })
                    .map(da => ({ value: da.id, label: da.displayName }))
            }

            return (
                <div key={a.id} className={(selectedDnpActivity.length ? 'highlight-unmapped ' : '') + "--row sm-right"}>
                    <span data-label={t('dnpActivities.labels.activity', {ns: 'configuration'})}><Link to={`/activity/${a.id}/information`} target="_blank">{a.displayName}</Link></span>
                    <span data-label={t('genericTableHeaders.school', {ns: 'common'})}><Link to={`/schoolunit/${a.schoolUnit.id}/information`} target="_blank">{a.schoolUnit.title}</Link></span>
                    <span data-label={t('dnpActivities.labels.grade', {ns: 'configuration'})}>{GetSchoolYear(a.schoolYear)}</span>
                    <span data-label={t('genericTableHeaders.startDate', {ns: 'common'})}>{Helper.formatDateOnly(a.startDate)}</span>
                    <span data-label={t('genericTableHeaders.endDate', {ns: 'common'})}>{Helper.formatDateOnly(a.endDate)}</span>
                    <span data-label={t('dnpActivities.labels.courseSubject', {ns: 'configuration'})}>{a.subjectTitle ? a.subjectTitle : a.courseTitle}</span>
                    <span>
                        <MultiSelectInput
                            items={dnpActivityOptions}
                            onChange={(name, value) => updateActivity(value, a)}
                            name='schoolType'
                            selected={selectedDnpActivity}
                            defaultText={t('dnpActivities.SelectInputText', {ns: 'configuration'})}
                            disabled={props.dnpActivities.code !== StatusCode.COMPLETE || props.disabled}
                        />
                    </span>
                </div>
            );
        })}
    </>);
}

export default UnmappedDnpActivities;
